import type { BookDetail } from "./book";

export function nextBookAction(book: BookDetail): { key: string; path: string | null } {
  const path = (segment: string) => `/books/${book.id}/${segment}`;
  if (book.state === "failed" || book.activeJobs.some((j) => j.manualRetryRequired))
    return { key: "retry", path: path("overview#failures") };
  if (!book.confirmedAt && book.state === "parsed" && book.metadataConfirmedAt === null)
    return { key: "metadata", path: path("overview#book-details") };
  const draft = book.drafts?.[0];
  if (draft) {
    const segment = draft.key.startsWith("chapter-")
      ? `review/${draft.key.slice(8)}`
      : ({
          metadata: "overview#book-details",
          settings: "render",
          chapters: "chapters",
          casting: "casting",
          guidance: "style-sheet",
        }[draft.key] ?? "overview");
    return { key: "applyDraft", path: path(segment) };
  }
  if (!book.confirmedAt)
    return {
      key: book.state === "parsed" ? "confirm" : "preparing",
      path: book.state === "parsed" ? path("chapters") : null,
    };
  if (!book.casting.narrator) return { key: "voice", path: path("casting") };
  const calibration = book.chapters.find((c) => c.isCalibration);
  if (calibration && !calibration.approvedAt) {
    const quality = book.qualityReviews?.find((q) => q.chapterId === calibration.id);
    if (
      quality?.status === "attention" ||
      quality?.status === "failed" ||
      quality?.status === "stale"
    )
      return { key: "exceptions", path: path(`review/${calibration.id}`) };
    if (calibration.versionCount > 0 && !quality && book.reviewPolicy === "exceptions")
      return { key: "check", path: path(`review/${calibration.id}`) };
    if (
      calibration.versionCount > 0 &&
      (book.reviewPolicy !== "exceptions" || quality?.status === "passed")
    )
      return { key: "calibration", path: path(`review/${calibration.id}`) };
    return { key: "preparing", path: null };
  }
  const exception = book.qualityReviews?.find((q) =>
    ["attention", "failed", "stale"].includes(q.status),
  );
  if (exception) return { key: "exceptions", path: path(`review/${exception.chapterId}`) };
  if (book.activeJobs.some((j) => j.status === "running" || j.status === "queued"))
    return {
      key: "working",
      path: book.render?.status === "rendering" ? path("render/progress") : null,
    };
  const pending = book.chapters.find((c) => !c.excluded && !c.approvedAt);
  if (pending)
    return {
      key: book.reviewPolicy === "exceptions" ? "check" : "review",
      path: path(`review/${pending.id}`),
    };
  if (book.audiobook && !book.audiobook.stale) return { key: "download", path: path("audiobook") };
  return { key: "render", path: path("render") };
}
